/**
 * The charts dashboard: its own ordered list of Yahoo symbols (separate from the watchlist), each
 * with a period and a style (candles or line), and the candles of each chart. Intraday charts refresh while the page is open.
 */
import { createEffect, createEvent, createStore, sample } from 'effector';
import { interval } from 'patronum';
import { api, unwrap, type Schemas } from '../../shared/api/client';

export type ChartItem = Schemas['ChartItem'];
export type ChartPeriod = ChartItem['period'];
export type ChartStyle = ChartItem['style'];
/** What a chart's data depends on (its style only changes how it's drawn). */
export type ChartKey = Pick<ChartItem, 'symbol' | 'period'>;
export type ChartData = Schemas['ChartOut'];

export const PERIODS: readonly ChartPeriod[] = ['1d', '5d', '1mo', '6mo', 'ytd', '1y', '5y', 'max'];
export const DEFAULT_PERIOD: ChartPeriod = '6mo';
export const DEFAULT_STYLE: ChartStyle = 'candles';
export const MAX_CHARTS = 24;
/** Ticks of the refresh timer between reloads, per period (none for daily and longer bars). */
const REFRESH_EVERY: Partial<Record<ChartPeriod, number>> = { '1d': 1, '5d': 1, '1mo': 5 };
const TICK_MS = 60_000;

export interface ChartState {
  data: ChartData | null;
  error: string | null;
  loading: boolean;
}

const EMPTY: ChartState = { data: null, error: null, loading: false };

export const chartsPageOpened = createEvent();
export const chartsPageClosed = createEvent();
export const periodChanged = createEvent<ChartKey>();
export const styleChanged = createEvent<Pick<ChartItem, 'symbol' | 'style'>>();
export const chartRetried = createEvent<ChartKey>();

export const loadLayoutFx = createEffect(() => unwrap(api.GET('/api/v1/charts/layout')));
export const saveLayoutFx = createEffect((items: ChartItem[]) =>
  unwrap(api.PUT('/api/v1/charts/layout', { body: { items } })),
);
export const loadChartFx = createEffect(({ symbol, period }: ChartKey) =>
  unwrap(api.GET('/api/v1/charts/data', { params: { query: { symbol, period } } })),
);
export const lookupSymbolFx = createEffect((symbol: string) =>
  unwrap(api.GET('/api/v1/charts/lookup', { params: { query: { symbol } } })),
);

export const $layout = createStore<ChartItem[] | null>(null)
  .on([loadLayoutFx.doneData, saveLayoutFx.doneData], (_, layout) => layout.items ?? [])
  .on(
    [periodChanged, styleChanged],
    (items, changed) =>
      items?.map((item) => (item.symbol === changed.symbol ? { ...item, ...changed } : item)) ??
      null,
  );

const chartLoaded = createEvent<{ symbol: string; data: ChartData }>();
const chartFailed = createEvent<{ symbol: string; error: string }>();

export const $charts = createStore<Record<string, ChartState>>({})
  .on(loadChartFx, (charts, { symbol }) => ({
    ...charts,
    [symbol]: { ...(charts[symbol] ?? EMPTY), loading: true },
  }))
  .on(chartLoaded, (charts, { symbol, data }) => ({
    ...charts,
    [symbol]: { data, error: null, loading: false },
  }))
  .on(chartFailed, (charts, { symbol, error }) => ({
    ...charts,
    [symbol]: { ...(charts[symbol] ?? EMPTY), error, loading: false },
  }));

/** Whether a finished request still matches what the chart shows (a newer period wins). */
function current(layout: ChartItem[] | null, { symbol, period }: ChartKey): boolean {
  return layout?.some((i) => i.symbol === symbol && i.period === period) ?? false;
}

sample({
  clock: loadChartFx.done,
  source: $layout,
  filter: (layout, { params }) => current(layout, params),
  fn: (_, { params, result }) => ({ symbol: params.symbol, data: result }),
  target: chartLoaded,
});

sample({
  clock: loadChartFx.fail,
  source: $layout,
  filter: (layout, { params }) => current(layout, params),
  fn: (_, { params, error }) => ({ symbol: params.symbol, error: error.message }),
  target: chartFailed,
});

const loadMany = createEffect((items: ChartItem[]) => {
  for (const item of items) void loadChartFx(item).catch(() => undefined);
});

sample({ clock: chartsPageOpened, target: loadLayoutFx });

// A loaded or saved layout fetches the charts that are missing or show another period.
sample({
  clock: [loadLayoutFx.doneData, saveLayoutFx.doneData],
  source: $charts,
  fn: (charts, layout) =>
    (layout.items ?? []).filter((item) => {
      const state = charts[item.symbol];
      return !state?.loading && state?.data?.period !== item.period;
    }),
  target: loadMany,
});

// A new period shows at once; periods and styles are remembered on the server.
sample({ clock: periodChanged, target: loadChartFx });
sample({
  clock: [periodChanged, styleChanged],
  source: $layout,
  filter: Boolean,
  target: saveLayoutFx,
});
// If remembering it failed, show what the server has.
sample({ clock: saveLayoutFx.fail, target: loadLayoutFx });

sample({ clock: chartRetried, target: loadChartFx });

// Intraday charts refresh while the page is open and visible.
const { tick } = interval({ timeout: TICK_MS, start: chartsPageOpened, stop: chartsPageClosed });
const $ticks = createStore(0)
  .on(tick, (n) => n + 1)
  .reset(chartsPageOpened);

sample({
  clock: $ticks.updates,
  source: $layout,
  filter: () => document.visibilityState === 'visible',
  fn: (layout, ticks) =>
    (layout ?? []).filter((item) => {
      const every = REFRESH_EVERY[item.period];
      return every !== undefined && ticks % every === 0;
    }),
  target: loadMany,
});
